"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  LAB_MANUAL_FIELDS as F,
  formatLabManual,
  type LabManualKind,
  type LabManualLabels,
  labManualIsReady,
} from "@/lib/tool-kit/citation-sources-more/lab-manual-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const LAB_MANUAL_CITATION_GENERATOR_STRINGS = {
  formHeading: "Lab manual details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the manual's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What kind of manual is it?",
  kindPublished: "Published (a press, an edition, an ISBN)",
  kindDepartmental: "A department's or instructor's own handout",
  authorsLabel: "Author(s)",
  authorsPlaceholder: "J. A. Beran",
  authorsHint:
    "One per line, as First Last or Last, First. Leave empty if the manual names no author — a department then stands in as the author.",
  organizationLabel: "The author is an organization, not a person",
  titleLabel: "Manual title",
  titlePlaceholder: "Laboratory manual for principles of general chemistry",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA, Chicago and ACS get title case automatically.",
  editionLabel: "Edition (optional)",
  editionPlaceholder: "11",
  editionHint: "A number such as 11, or words such as Revised.",
  yearLabel: "Year",
  yearPlaceholder: "2022",
  yearHint:
    "The year on the copyright line or cover. For a school year such as 2025–2026, use the later year.",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Wiley",
  docTypeLabel: "The document is a",
  docTypeManual: "Lab manual",
  docTypeHandout: "Lab handout",
  departmentLabel: "Department",
  departmentPlaceholder: "Department of Chemistry",
  universityLabel: "University or college",
  universityPlaceholder: "Example University",
  siteLabel: "Course site, if you read it there (optional)",
  sitePlaceholder: "Canvas@Example University",
  siteHint:
    "Canvas, Blackboard, Moodle. Name the site only if your readers can sign in to it.",
  urlLabel: "URL (optional)",
  urlPlaceholder: "https://example.instructure.com/login",
  urlHint: "For a course site, the login page rather than a link behind it.",
  formatLabel: "File format (Chicago, optional)",
  formatPlaceholder: "PDF",
  pagesLabel: "Page(s) you cite (optional)",
  pagesPlaceholder: "12",
  experimentLabel: "Experiment or section (APA, optional)",
  experimentPlaceholder: "Experiment 3",
  experimentHint:
    "APA lets you name the part in the in-text citation. MLA and Chicago cite pages, so name the experiment in your sentence.",
  shortTitleLabel: "Short title for notes (Chicago, optional)",
  shortTitlePlaceholder: "Laboratory Manual",
  shortTitleHint:
    "Chicago shortens a long title to its key words, by hand. Leave blank for a suggestion.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  acsLabel: "ACS (American Chemical Society)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 has no lab-manual example. A published manual is cited as a book. A department's own manual gets a bracketed description, and the university as publisher.",
  mlaNote:
    "MLA 9 begins with the author only when the author is known. A department that is also the publisher is not repeated as the author, so the entry starts with the title.",
  chicagoNote:
    "Chicago 18 treats a published manual as a book. A manual nobody else can obtain follows the unpublished-manuscript pattern (§14.114) and normally stays in a note, out of the bibliography.",
  acsNote:
    "ACS has no lab-manual example. This applies its book template, with the university as publisher for a department's own manual. ACS cites by number in the text, so there is no in-text form to copy.",
  examplePublished: {
    authors: "J. A. Beran\nMark Lassiter",
    title: "Laboratory manual for principles of general chemistry",
    edition: "11",
    year: "2022",
    publisher: "Wiley",
    pages: "12",
    experiment: "Experiment 3",
  },
  exampleDepartmental: {
    title: "General chemistry I laboratory manual",
    year: "2025",
    docType: "manual",
    department: "Department of Chemistry",
    university: "Example University",
    format: "PDF",
    pages: "12",
    shortTitle: "General Chemistry I",
  },
} as const;

type Strings = typeof LAB_MANUAL_CITATION_GENERATOR_STRINGS;

function leadFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.organization,
      kind: "checkbox",
      label: strings.organizationLabel,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.edition,
      kind: "text",
      label: strings.editionLabel,
      placeholder: strings.editionPlaceholder,
      hint: strings.editionHint,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
      hint: strings.yearHint,
    },
  ];
}

function publisherFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
  ];
}

function departmentalFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.docType,
      kind: "select",
      label: strings.docTypeLabel,
      options: [
        { value: "manual", label: strings.docTypeManual },
        { value: "handout", label: strings.docTypeHandout },
      ],
    },
    {
      id: F.department,
      kind: "text",
      label: strings.departmentLabel,
      placeholder: strings.departmentPlaceholder,
    },
    {
      id: F.university,
      kind: "text",
      label: strings.universityLabel,
      placeholder: strings.universityPlaceholder,
    },
    {
      id: F.site,
      kind: "text",
      label: strings.siteLabel,
      placeholder: strings.sitePlaceholder,
      hint: strings.siteHint,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      hint: strings.urlHint,
    },
    {
      id: F.format,
      kind: "text",
      label: strings.formatLabel,
      placeholder: strings.formatPlaceholder,
    },
  ];
}

function locatorFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.pages,
      kind: "text",
      label: strings.pagesLabel,
      placeholder: strings.pagesPlaceholder,
    },
    {
      id: F.experiment,
      kind: "text",
      label: strings.experimentLabel,
      placeholder: strings.experimentPlaceholder,
      hint: strings.experimentHint,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
      hint: strings.shortTitleHint,
    },
  ];
}

function buildFields(strings: Strings, kind: LabManualKind): SourceField[] {
  const middle =
    kind === "published"
      ? publisherFields(strings)
      : departmentalFields(strings);
  return [...leadFields(strings), ...middle, ...locatorFields(strings)];
}

/** The widget's strings, as the formatter's labels. */
export function labManualLabels(strings: Strings): LabManualLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    acs: strings.acsLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
    acsNote: strings.acsNote,
  };
}

export default function LabManualCitationGeneratorWidget() {
  const strings = useToolUi(LAB_MANUAL_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<LabManualKind>("published");
  const fields = useMemo(() => buildFields(strings, kind), [strings, kind]);
  const labels = useMemo(() => labManualLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatLabManual(kind, values, labels),
    [kind, labels]
  );

  return (
    <SourceCitationBuilder
      example={
        kind === "published"
          ? strings.examplePublished
          : strings.exampleDepartmental
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="lab-manual-kind"
          onChange={setKind}
          options={[
            { value: "published", label: strings.kindPublished },
            { value: "departmental", label: strings.kindDepartmental },
          ]}
          value={kind}
        />
      }
      isReady={labManualIsReady}
      strings={strings}
    />
  );
}
